/**
 * `upload` — FR-13, FR-14, §2.4.2 Path B (guided manual upload).
 *
 * ## FR-14 in the type system: no free-text keys
 *
 * The contract key is **not an input**. It is minted server-side from the selected
 * primary party and the sequence number the server offered — which is why
 * {@link UploadOptionsResponseSchema} exists and why {@link UploadRequestSchema}
 * has no `contract_key` field. Since D-110-1 (CLM-110) a new key is
 * `PartyKey-nnnn` and carries **no type**: one series per party, whatever the
 * types filed under it.
 *
 * ## Nobody is asked for a type (CLM-118, D-110-3)
 *
 * The request carries **no type** (B9). The server resolves the stored type by
 * the first rule that applies — an `amends` target's type (`inherited`), a
 * `supersedes` target's type, the client file name's type (a type word, or a
 * SOW-shaped number), a typed SOW number (`SOW`, never trusted), or **Type
 * pending** (`OTHER` + `pending`) — and records what the file name gave as
 * `contract_type_name_evidence`. The AI's reading then confirms a file-name type
 * it agrees with, or supplies the type where the name gave none, for an Admin
 * to verify. The response carries what was resolved.
 *
 * FR-14 exists because hand-typed identifiers are the documented failure mode:
 * *"guided selection of party, type, parent"*. A `contract_key: string` on this
 * request would satisfy the letter of the requirement while reintroducing exactly
 * the error it prevents, so it is absent and the guided flow is the only path.
 *
 * Path A (DocuSign Connect, §2.4.1) has no endpoint here: the key is entered in a
 * custom envelope field at *send* time and arrives on the webhook. That is what
 * makes FR-29 deterministic — the party is known before any reading happens.
 */
import { z } from 'zod';
import { ContractKeySchema, HttpsUrlSchema, IsoDateSchema, PartyKeySchema, Sha256Schema, } from '../primitives.js';
import { AmendmentSignalNameSchema, ContractPartyRoleSchema, ContractTypeConfirmationSchema, ContractTypeDecidedBySchema, ContractTypeNameEvidenceSchema, ContractTypeRatifiedSchema, ContractTypeSchema, PartyTypeSchema, ProcessingStatusSchema, SecurityClassificationSchema, } from '../vocabulary.js';
import { AuditReceiptSchema } from '../provenance.js';
import { PageInfoSchema, PageRequestSchema } from '../pagination.js';
import { TAGS_PER_CONTRACT_MAX, TagSchema } from './contract-tags.js';
/* -------------------------------------------------------------------------- */
/* Step 1 — options for the guided form                                        */
/* -------------------------------------------------------------------------- */
export const UploadOptionsRequestSchema = z
    .object({
    /**
     * Filters the party list. Server-side scope still applies: a party outside
     * the caller's scope never appears in the options, so it can never be
     * selected, so a contract can never be filed against it.
     */
    party_search: z.string().min(1).max(200).optional(),
    /**
     * Supply once the user has chosen a party, to get parent candidates and the key
     * preview. CLM-110: the party alone is enough — keys carry no type.
     */
    primary_party_key: PartyKeySchema.optional(),
    /**
     * CLM-118 (R-118-18) — the selected file's own name, so the server reads the
     * type it gives (`name_type`) and narrows each candidate's
     * `valid_relationships` by the pair rules (B16). Evidence only: never a path,
     * never stored. Without it a candidate offers every relationship its own type
     * and lifecycle permit.
     */
    client_file_name: z.string().min(1).max(255).optional(),
    /**
     * CLM-78 (B3) — narrow the party list to one type; `'Vendor'` is what the
     * `VendorPicker` sends. Scope still applies underneath (rule 9).
     */
    party_type: PartyTypeSchema.optional(),
    /**
     * Pages `parties` only (CLM-68). Keyset over `(display_name, party_key)`;
     * `party_search` is part of the cursor fingerprint, so a cursor minted for
     * one term is refused for another. On the wire the fields travel as the
     * dotted keys `page.limit` / `page.cursor`.
     */
    page: PageRequestSchema.optional(),
})
    .strict();
export const SelectablePartySchema = z.object({
    party_key: PartyKeySchema,
    display_name: z.string().min(1).max(200),
    party_type: PartyTypeSchema,
    /**
     * §4C — inherited from the party. Surfaced here so the form can warn *before*
     * upload that this contract will not be AI-processed and its facts will have to
     * be entered by hand.
     */
    processing_restricted: z.boolean(),
    /**
     * CLM-114 · R-114-3 (a) — when the search matched an **active alias** and not
     * the display name, that alias's text, shown as "also known as …" so a person
     * who typed a trading name can see why a differently named vendor came up;
     * `null` when the display name matched. Optional, so a web deployed before
     * the API still parses: either deploy order is safe.
     */
    matched_alias: z.string().min(1).max(200).nullable().optional(),
    /**
     * CLM-126 — present only on a nearest-match candidate (never on an ordinary
     * search result): the trigram `similarity()` score, in `[0, 1]`, rounded to
     * 3 decimals. The UI's cue to render "~NN% match" beside the name. Optional
     * and nullable, same back-compat posture as `matched_alias`.
     */
    suggestion_score: z.number().min(0).max(1).nullable().optional(),
});
/**
 * A contract the new upload may legitimately point at.
 *
 * The server computes this list, not the client. §8.1 Rule 1 — an amendment may
 * only target an agreement that is signed and not ended (`Executed` or `Active`,
 * D-124-1) — is a cross-row check, so offering only valid targets is the
 * difference between a disabled option and a rejected submission.
 */
export const CandidateParentSchema = z.object({
    contract_key: ContractKeySchema,
    display_label: z.string().min(1).max(300),
    /**
     * CLM-110 — the candidate's own type and its settlement. An `amends` or
     * `supersedes` filing takes this type (R-110-16), so the form shows "Follows
     * {type}" from it.
     */
    contract_type: ContractTypeSchema,
    contract_type_confirmation: ContractTypeConfirmationSchema,
    /**
     * CLM-110 (R-110-16) — whether the chosen party is this candidate's **primary**
     * party. `amends` is offered only when it is: the amendment's key, file name and
     * primary `contract_party` row must all name the agreement's own party
     * (`party_follows_agreement`).
     */
    party_is_primary: z.boolean(),
    /** Which of the three §8.1 relationships this candidate is valid for. */
    valid_relationships: z
        .array(z.enum(['child_of', 'amends', 'supersedes']))
        .min(1),
});
/**
 * CLM-118 (R-118-5, R-118-14) — what the deterministic file-name rule reads from
 * `client_file_name`, for the form's name line: the evidence, the type it gives
 * (`null` unless `typed`), the first SOW-shaped number in the name (to pre-fill
 * the SOW-number field), and the amendment word, if any.
 */
export const UploadNameTypeSchema = z
    .object({
    evidence: ContractTypeNameEvidenceSchema,
    contract_type: ContractTypeRatifiedSchema.nullable(),
    sow_number_candidate: z.string().min(1).max(32).nullable(),
    amendment_signal: AmendmentSignalNameSchema.nullable(),
})
    .strict()
    .refine((name) => (name.contract_type !== null) === (name.evidence === 'typed'), {
    message: 'a type is given exactly when the evidence is typed',
    path: ['contract_type'],
});
export const UploadOptionsResponseSchema = z.object({
    parties: z.array(SelectablePartySchema),
    /**
     * Paging for `parties` alone (CLM-68). `total_in_scope` is always `null` — no
     * count query at rule-17 volume; the picker only needs "is there more".
     */
    parties_page: PageInfoSchema,
    candidate_parents: z.array(CandidateParentSchema),
    /** CLM-118 — what the file name gives; `null` when no `client_file_name` was sent. */
    name_type: UploadNameTypeSchema.nullable(),
    /**
     * The next free sequence number for `primary_party_key` — one series per party,
     * counting both key shapes (CLM-110, R-110-10). `null` until a party is
     * supplied, and when the party's four-digit series is used up.
     *
     * Advisory: the server re-derives and re-validates it on submission, because two
     * users opening the form at once would otherwise both be offered the same number.
     */
    next_sequence_number: z.number().int().min(1).nullable(),
    /** Preview of the key that will be minted (`PartyKey-nnnn`), for the form to display. */
    projected_contract_key: ContractKeySchema.nullable(),
    accepted_media_types: z.array(z.literal('application/pdf')),
    max_document_bytes: z.number().int().min(1),
});
/* -------------------------------------------------------------------------- */
/* Refusal reasons — CLM-110 (R-110-10, R-110-16)                              */
/* -------------------------------------------------------------------------- */
/**
 * The `409` reasons an upload answers, as the first `field_errors` message's
 * `reason:` prefix and a bundle row's `reason`. Each says something different to
 * act on:
 *
 *  - `sow_number_in_use` — the number is held outside this family;
 *  - `sequence_number_stale` — the mint returned a different number from the one
 *    the form offered: re-open the form;
 *  - `key_collision` — the minted key is already held, by a contract outside the
 *    caller's scope or a writer that skipped the lock. Re-opening the form would
 *    offer the same number, so this is never `sequence_number_stale` (R-110-10);
 *  - `agreement_type_changed` — the `amends` / `supersedes` target's type or
 *    confirmation changed between the form and the filing: an Admin decided it,
 *    or (CLM-118) the pipeline's type step confirmed it because the file name and
 *    the AI's reading agree, or applied the AI's reading as its working type.
 */
export const UPLOAD_CONFLICT_REASONS = [
    'sow_number_in_use',
    'sequence_number_stale',
    'key_collision',
    'agreement_type_changed',
];
/**
 * The `400` reasons of the type resolution (R-110-16 as CLM-118 amends it),
 * checked on the server before any Graph call:
 *
 *  - `party_follows_agreement` — an `amends` whose party is not the agreement's
 *    primary party;
 *  - `relationship_not_valid` — the pair rules (`child_of` an MSA, `amends` an
 *    `Executed` or `Active` agreement of the same type, never an amendment of
 *    one, …) refuse the relationship;
 *  - `sow_number_required` / `sow_number_not_applicable` — the SOW-number pair,
 *    both ways, on the resolved type;
 *  - `sow_number_conflicts_with_name` — CLM-118 (R-118-6, B10): a typed SOW
 *    number beside a file name that says MSA or NDA, or that names more than one
 *    type.
 *
 * `type_follows_agreement` is retired with the hint it refused (R-118-18).
 */
export const UPLOAD_TYPE_REFUSAL_REASONS = [
    'party_follows_agreement',
    'relationship_not_valid',
    'sow_number_required',
    'sow_number_not_applicable',
    'sow_number_conflicts_with_name',
];
/* -------------------------------------------------------------------------- */
/* Step 2 — the upload itself                                                  */
/* -------------------------------------------------------------------------- */
/**
 * How this contract relates to what already exists — §8.1, DR-02.
 *
 * A discriminated union, because the three relationships are not interchangeable
 * and a nullable trio of keys would allow `amends` and `supersedes` to be set at
 * once (which `CK_Contract_AmendXorSupersede` forbids).
 */
export const UploadRelationshipSchema = z.discriminatedUnion('relationship', [
    z.object({ relationship: z.literal('standalone') }).strict(),
    z
        .object({
        /** Structural: a SOW under its MSA. */
        relationship: z.literal('child_of'),
        target_contract_key: ContractKeySchema,
    })
        .strict(),
    z
        .object({
        /**
         * Modifies. The agreement keeps its own status; the governing text is parent
         * plus amendments in sequence. The new contract inherits owner, security and
         * category from the target, and any values sent for them are ignored in
         * favour of the agreement's. It is linked to the agreement's parties as well
         * as to any `additional_parties` sent (§8.1 Rule 4, CLM-124 R-124-12). It is
         * `processing_restricted` whenever the agreement is (§4C, R-124-13).
         */
        relationship: z.literal('amends'),
        target_contract_key: ContractKeySchema,
    })
        .strict(),
    z
        .object({
        /** Replaces. The target flips to `Superseded` and becomes read-only (Rule 2). */
        relationship: z.literal('supersedes'),
        target_contract_key: ContractKeySchema,
    })
        .strict(),
]);
/**
 * Descriptor of the attached document.
 *
 * The bytes travel as a `multipart/form-data` part named `document`; this is the
 * metadata part, named `metadata`, carrying `application/json`. The bytes are
 * never base64-inlined in JSON — a 20 MB PDF through a JSON body is a 27 MB
 * string that has to be held in memory twice.
 */
export const UploadDocumentDescriptorSchema = z
    .object({
    file_name: z.string().min(1).max(255),
    /**
     * PDF only.
     *
     * Not a limitation to relax casually: conversion must precede pseudonymisation
     * because substitution operates on *text* (§2.2), and the conversion service is
     * specified against Document Intelligence Read over PDF (§4B.3). Another input
     * format is a new conversion path, and therefore a new FR-29 analysis.
     */
    media_type: z.literal('application/pdf'),
    size_bytes: z.number().int().min(1),
    /** Evidences which bytes were stored. Verified server-side against the part. */
    sha256: Sha256Schema,
})
    .strict();
export const UploadRequestSchema = z
    .object({
    /* Selected, never typed — FR-14. No type: the server resolves it (CLM-118, B9). */
    primary_party_key: PartyKeySchema,
    /** Echoed from `next_sequence_number`. The server re-validates it. */
    sequence_number: z.number().int().min(1),
    relationship: UploadRelationshipSchema,
    /** FR-09 — additional parties beyond the primary. */
    additional_parties: z
        .array(z
        .object({
        party_key: PartyKeySchema,
        party_role: ContractPartyRoleSchema,
    })
        .strict())
        .optional(),
    /** CLM-77 — normalised tags stored in the same transaction as the contract row (R-77-1). */
    tags: z.array(TagSchema).max(TAGS_PER_CONTRACT_MAX).optional(),
    document: UploadDocumentDescriptorSchema,
    /**
     * Known from the paper before extraction runs; provenance will record these as
     * `intake_metadata`. Everything else is left to the pipeline — FR-13 is a
     * controlled intake path, not a manual data-entry form.
     */
    execution_date: IsoDateSchema.optional(),
    effective_date: IsoDateSchema.optional(),
    security_classification: SecurityClassificationSchema.optional(),
    category: z.string().min(1).max(60).optional(),
    /**
     * §4C — per-contract override of the party-level flag. Set when *this
     * contract's own terms* forbid third-party processing.
     *
     * Setting it is a compliance act: the contract is still stored, converted
     * in-tenant, indexed and searchable, but no LLM will ever see it and its facts
     * must be entered by hand. Omit to inherit the party's setting; it can widen
     * the restriction but never lift one the party imposes.
     */
    processing_restricted_override: z.literal(true).optional(),
    processing_restriction_reason: z.string().min(1).max(1000).optional(),
    /**
     * IR-01 — reused from the source system, never minted here. Required when the
     * file name gives `SOW` (D-71-0), and giving one when the name gives no type
     * files the contract as a SOW (never a trusted file-name type, R-118-4). The
     * server checks the pair both ways on the **resolved** type, which only it
     * knows: `sow_number_required`, `sow_number_not_applicable` and
     * `sow_number_conflicts_with_name`. An amendment of a SOW defaults to its
     * agreement's number.
     */
    sow_number: z.string().min(1).max(32).optional(),
    /**
     * CLM-78 (R-76-2) — what the form said the counterparty is. **Request-time
     * only, never stored** (K5: vendor-ness is `party.party_type` via
     * `contract_party`). The API answers 400 `counterparty_kind_mismatch` when it
     * disagrees with the selected party's type (`vendor` ⇔ `'Vendor'`).
     */
    counterparty_kind: z.enum(['client', 'vendor']).optional(),
})
    .strict();
/**
 * `202 Accepted`, not `201 Created`.
 *
 * The row exists and the document is stored, but the facts do not exist yet: the
 * pipeline is queued and runs conversion → pseudonymisation → extraction
 * asynchronously. Returning `201` with a contract whose dates are all `null` would
 * invite the front end to render it as complete.
 */
export const UploadResponseSchema = z.object({
    /** Minted server-side from party + sequence — `PartyKey-nnnn` (D-110-1). */
    contract_key: ContractKeySchema,
    /**
     * CLM-110 (R-110-16), CLM-118 — the type the server **resolved** and stored,
     * how, and whether it is settled. The success panel and the CLM-112
     * base-deviation offer read these. Settled (`confirmed` / `auto_confirmed`)
     * only for an `amends` of a settled agreement (`inherited`); `pending` is Type
     * pending.
     */
    contract_type: ContractTypeSchema,
    contract_type_source: ContractTypeDecidedBySchema,
    contract_type_confirmation: ContractTypeConfirmationSchema,
    /**
     * CLM-118 (R-118-3) — what the file name gave, as stored: an `amends` or
     * `supersedes` filing is `typed` when it follows an MSA, NDA or SOW (§3.5 rule 1).
     */
    contract_type_name_evidence: ContractTypeNameEvidenceSchema,
    /**
     * `PendingExtraction` normally; `manual_entry_pending` where the contract is
     * `processing_restricted` and the facts must be entered by hand (§4C).
     */
    processing_status: ProcessingStatusSchema,
    /** Where the bytes landed. IR-05 — the document stays in SharePoint. */
    sharepoint_item_url: HttpsUrlSchema,
    /** True when this upload will never be sent to an LLM. */
    processing_restricted: z.boolean(),
    /** Poll `retrieve-contract`; extraction is asynchronous by design. */
    extraction_queued: z.boolean(),
    audit: AuditReceiptSchema,
});
//# sourceMappingURL=upload.js.map